import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assemblyService } from 'services/api';

// Lógica de la pantalla (estado, carga y acciones); la página solo pinta.
export default function useAssemblyForm() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const isEdit      = !!id;

  const [form, setForm] = useState({
    property_id: '', number_assembly: '',
    // Quórum por defecto: mayoría absoluta (50%+1, es decir MÁS de la mitad)
    // de primera convocatoria — es el mismo para Ordinaria y Extraordinaria
    // por temas comunes según Ley 675 (Art. 47), así que no varía según el
    // tipo elegido. Se usa 51, no 50: el sistema compara con >= (ver
    // quorumRequerido en votes.js), así que 50 dejaría pasar un empate exacto
    // de 50% como si fuera quórum válido, cuando la ley exige superarlo.
    // Queda editable porque el admin sí lo puede necesitar ajustar (segunda
    // convocatoria, o un tema puntual con mayoría calificada del 70%), casos
    // que el sistema no modela todavía.
    date: '', quorum: isEdit ? '' : 51,
    assembly_type: 'ordinaria', assembly_model: 'virtual',
    url: '', start_date: '', end_date: '', status: 'pendiente',
    orden_del_dia: '',
  });

  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [numberLoading, setNumberLoading] = useState(false);

  // ── Validación de unidades sin propietario ────────────────────────────────
  const [checkingUnits,   setCheckingUnits]   = useState(false);
  const [unitsWarning,    setUnitsWarning]    = useState(null);
  // { total, withOwner, withoutOwner, details: [{tower, unit}] }

  useEffect(() => {
    // AUD-03: el plan "Asamblea + Logística" no incluye este módulo — el
    // backend ya rechaza crear una asamblea ahí, pero antes el selector la
    // listaba igual, dejando que el admin llenara todo el formulario para
    // recién enterarse del rechazo al final.
    if (isEdit) {
      assemblyService.getById(id).then(r => {
        const a   = r.data;
        const fmt = (d) => d ? new Date(d).toISOString().slice(0, 16) : '';
        setForm({
          property_id:     a.property_id     || '',
          number_assembly: a.number_assembly || '',
          date:            fmt(a.date),
          quorum:          a.quorum          || '',
          assembly_type:   a.assembly_type   || 'ordinaria',
          assembly_model:  a.assembly_model  || 'virtual',
          url:             a.url             || '',
          start_date:      fmt(a.start_date),
          end_date:        fmt(a.end_date),
          status:          a.status          || 'pendiente',
          orden_del_dia:   a.orden_del_dia   || '',
        });
      });
    }
  }, [id, isEdit]);

  // ── Validar unidades al seleccionar/cambiar propiedad ─────────────────────
  const checkUnitsForProperty = useCallback(async (propertyId) => {
    if (!propertyId || isEdit) { setUnitsWarning(null); return; }

    setCheckingUnits(true);
    setUnitsWarning(null);
    try {
      // Un solo pedido: el servidor cuenta en la base de datos, sin topes de torres ni de unidades.
      const { data } = await assemblyService.verificarUnidades(propertyId);
      if (data.torres === 0) setUnitsWarning({ type: 'no_buildings' });
      else if (data.total === 0) setUnitsWarning({ type: 'no_units' });
      else if (data.sin_propietario > 0) {
        setUnitsWarning({
          type:         'missing_owners',
          total:        data.total,
          withOwner:    data.total - data.sin_propietario,
          withoutOwner: data.sin_propietario,
          details:      data.detalle.map(d => ({ tower: d.torre, unit: d.unidad })),
          truncated:    data.detalle_truncado,
        });
      } else {
        setUnitsWarning({ type: 'ok', total: data.total });
      }
    } catch {
      // Si no se pudo verificar NO se da el visto bueno en silencio: se avisa y se puede reintentar.
      setUnitsWarning({ type: 'error' });
    } finally {
      setCheckingUnits(false);
    }
  }, [isEdit]);

  // Número de Asamblea autoincremental por conjunto — MAX(number_assembly)+1
  // calculado en el backend (ver GET /assemblies/next-number). Solo aplica al
  // crear: en edición el número ya es un dato histórico, no se recalcula.
  const fetchNextNumber = useCallback(async (propertyId) => {
    if (!propertyId || isEdit) return;
    setNumberLoading(true);
    try {
      const res = await assemblyService.getNextNumber(propertyId);
      setForm(f => ({ ...f, number_assembly: res.data.next_number }));
    } catch {
      setForm(f => ({ ...f, number_assembly: '' }));
    } finally {
      setNumberLoading(false);
    }
  }, [isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (name === 'property_id') { checkUnitsForProperty(value); fetchNextNumber(value); }
  };

  // noValidate en el <form>: estas revisiones reemplazan el globo del navegador (mismas reglas que el servidor: cuórum entero de 1 a 100, número entero > 0).
  const validar = () => {
    if (!isEdit && !form.property_id) return 'Selecciona el conjunto o propiedad.';
    if (!Number.isInteger(Number(form.number_assembly)) || Number(form.number_assembly) < 1 || String(form.number_assembly).trim() === '')
      return isEdit ? 'El número de asamblea debe ser un entero mayor a 0.' : 'Elige el conjunto primero: el número de asamblea se asigna automáticamente.';
    if (!form.date) return 'Indica la fecha de la asamblea.';
    if (String(form.quorum).trim() === '' || !Number.isInteger(Number(form.quorum)) || Number(form.quorum) < 1 || Number(form.quorum) > 100)
      return 'El cuórum debe ser un porcentaje entero entre 1 y 100.';
    if (!form.start_date) return 'Indica la hora de inicio.';
    if (!form.end_date) return 'Indica la hora de fin.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const faltante = validar();
    if (faltante) { setError(faltante); window.scrollTo({ top: 0 }); return; }
    // Bloquear si no se pudo verificar las unidades
    if (!isEdit && unitsWarning && unitsWarning.type === 'error') {
      setError('No pudimos verificar las unidades de esta propiedad. Vuelve a intentarlo antes de crear la asamblea.');
      return;
    }
    // Bloquear si hay unidades sin propietario
    if (unitsWarning && unitsWarning.type === 'missing_owners') {
      setError('No puedes crear la asamblea hasta que todas las unidades tengan propietario asignado.');
      return;
    }
    // F-24: la hora de fin debe ser posterior a la de inicio (también lo valida el servidor).
    if (form.start_date && form.end_date && new Date(form.end_date) <= new Date(form.start_date)) {
      setError('La hora de fin debe ser posterior a la hora de inicio.');
      return;
    }
    setError(''); setLoading(true);
    try {
      const r = isEdit ? await assemblyService.update(id, form) : await assemblyService.create(form);
      // F-24: al reprogramar, el servidor avisa si algún representante con poder
      // quedó con otra asamblea a la misma hora — se muestra en la lista.
      const avisos = r?.data?.avisos_cruce;
      navigate('/admin/asambleas', avisos?.length ? { state: { avisoCruce: avisos } } : undefined);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = !checkingUnits && (!unitsWarning || unitsWarning.type === 'ok');

  return {
    form, loading, error, numberLoading, checkingUnits, unitsWarning, id, isEdit, checkUnitsForProperty,
    handleChange, handleSubmit, canSubmit,
  };
}
